/**
 * JWT Token Encryption Utilities
 * Encrypts tokens before storing in localStorage to mitigate XSS risks
 */

import CryptoJS from 'crypto-js';

// Encryption key - in production, this should be unique per deployment
// Note: This is NOT a complete XSS solution, but adds a layer of defense
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'gemini-image-gen-2026-key-change-in-prod';

/**
 * Encrypt token before storing
 * @param token - JWT token to encrypt
 * @returns Encrypted token string
 */
export function encryptToken(token: string): string {
  if (!token) return '';
  
  try {
    return CryptoJS.AES.encrypt(token, ENCRYPTION_KEY).toString();
  } catch (error) {
    console.error('Token encryption failed:', error);
    return token; // Fallback to unencrypted if encryption fails
  }
}

/**
 * Decrypt token after retrieving from storage
 * @param encryptedToken - Encrypted token string
 * @returns Decrypted JWT token
 */
export function decryptToken(encryptedToken: string): string {
  if (!encryptedToken) return '';
  
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedToken, ENCRYPTION_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    
    // If decryption fails, return empty string
    if (!decrypted) {
      console.warn('Token decryption returned empty string');
      return '';
    }
    
    return decrypted;
  } catch (error) {
    console.error('Token decryption failed:', error);
    // Might be an unencrypted token (migration case)
    // Try to use it as-is if it looks like a JWT
    if (encryptedToken.split('.').length === 3) {
      console.warn('Using potentially unencrypted token');
      return encryptedToken;
    }
    return '';
  }
}

/**
 * Securely store token in localStorage with encryption
 * @param key - Storage key
 * @param token - Token to store
 */
export function setSecureToken(key: string, token: string): void {
  if (!token) {
    localStorage.removeItem(key);
    return;
  }
  
  const encrypted = encryptToken(token);
  localStorage.setItem(key, encrypted);
}

/**
 * Retrieve and decrypt token from localStorage
 * @param key - Storage key
 * @returns Decrypted token or null
 */
export function getSecureToken(key: string): string | null {
  const encrypted = localStorage.getItem(key);
  if (!encrypted) return null;
  
  const decrypted = decryptToken(encrypted);
  return decrypted || null;
}

/**
 * Remove token from localStorage
 * @param key - Storage key
 */
export function removeSecureToken(key: string): void {
  localStorage.removeItem(key);
}

/**
 * Check if a token is encrypted
 * @param token - Token to check
 * @returns True if token appears to be encrypted
 */
export function isTokenEncrypted(token: string): boolean {
  if (!token) return false;
  
  // JWT tokens have 3 parts separated by dots
  // Encrypted tokens will not match this pattern
  const jwtPattern = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/;
  return !jwtPattern.test(token);
}

